import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { portalAppointmentsQueryKey } from '@/hooks/usePortalAppointments';
import {
  parsePortalTreatmentCourse,
  parsePortalTreatmentCourseList,
  type PortalTreatmentCourse,
} from '@/lib/portalTreatmentCourse';
import type { TreatmentCourseStepSlot } from '@/hooks/useBookTreatmentCourse';
import { portalTreatmentCourseSlotsQueryKey } from '@/lib/treatmentCourseSlotSearchKey';

export { portalTreatmentCourseSlotsQueryKey } from '@/lib/treatmentCourseSlotSearchKey';

export const portalTreatmentCoursesQueryKey = (clinicId: string | null) =>
  ['portal', 'treatment-courses', clinicId] as const;

export const portalTreatmentCourseQueryKey = (courseId: string | null) =>
  ['portal', 'treatment-course', courseId] as const;

export function usePortalTreatmentCourses() {
  const { activeClinicId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalTreatmentCoursesQueryKey(activeClinicId),
    enabled: Boolean(activeClinicId),
    queryFn: async (): Promise<PortalTreatmentCourse[]> => {
      if (!activeClinicId) return [];
      const { data, error } = await supabase.rpc('patient_list_treatment_courses', {
        p_clinic_id: activeClinicId,
      });
      if (error) throw error;
      return parsePortalTreatmentCourseList(data);
    },
    staleTime: 15_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalTreatmentCourse(courseId: string | null | undefined) {
  return useQuery({
    queryKey: portalTreatmentCourseQueryKey(courseId ?? null),
    enabled: Boolean(courseId),
    queryFn: async (): Promise<PortalTreatmentCourse | null> => {
      if (!courseId) return null;
      const { data, error } = await supabase.rpc('patient_get_treatment_course', {
        p_course_id: courseId,
      });
      if (error) throw error;
      return parsePortalTreatmentCourse(data);
    },
    staleTime: 15_000,
  });
}

export function usePatientTreatmentCourseStepSlots(
  input: {
    courseId: string;
    stepId: string;
    windowStart?: string | null;
    windowEnd?: string | null;
    practitionerIds?: string[] | null;
  } | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: portalTreatmentCourseSlotsQueryKey(input),
    enabled: Boolean(enabled && input?.courseId && input.stepId),
    queryFn: async (): Promise<TreatmentCourseStepSlot[]> => {
      if (!input) return [];
      const { data, error } = await supabase.rpc('patient_find_treatment_course_step_slots', {
        p_course_id: input.courseId,
        p_step_id: input.stepId,
        p_window_start: input.windowStart ?? undefined,
        p_window_end: input.windowEnd ?? undefined,
        p_practitioner_ids:
          input.practitionerIds && input.practitionerIds.length > 0
            ? input.practitionerIds
            : undefined,
      });
      if (error) throw error;
      return (data ?? []) as TreatmentCourseStepSlot[];
    },
  });
}

export function usePatientBookTreatmentCourseStep() {
  const queryClient = useQueryClient();
  const { activeClinicId, activePatientId } = usePortalActiveClinic();

  return useMutation({
    mutationFn: async (input: {
      courseId: string;
      stepId: string;
      startsAt: string;
      practitionerIds?: string[];
    }): Promise<PortalTreatmentCourse | null> => {
      const { data, error } = await supabase.rpc('patient_book_treatment_course_step', {
        p_course_id: input.courseId,
        p_step_id: input.stepId,
        p_starts_at: input.startsAt,
        p_practitioner_ids:
          input.practitionerIds && input.practitionerIds.length > 0
            ? input.practitionerIds
            : undefined,
      });
      if (error) throw error;
      return parsePortalTreatmentCourse(data);
    },
    onSuccess: (course) => {
      void queryClient.invalidateQueries({
        queryKey: portalTreatmentCoursesQueryKey(activeClinicId),
      });
      void queryClient.invalidateQueries({
        queryKey: portalAppointmentsQueryKey(activePatientId),
      });
      if (course?.id) {
        queryClient.setQueryData(portalTreatmentCourseQueryKey(course.id), course);
      }
    },
  });
}

export function usePatientCancelTreatmentCourseStep() {
  const queryClient = useQueryClient();
  const { activeClinicId, activePatientId } = usePortalActiveClinic();

  return useMutation({
    mutationFn: async (input: {
      stepId: string;
      reason?: string;
    }): Promise<PortalTreatmentCourse | null> => {
      const { data, error } = await supabase.rpc('patient_cancel_treatment_course_step', {
        p_step_id: input.stepId,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return parsePortalTreatmentCourse(data);
    },
    onSuccess: (course) => {
      void queryClient.invalidateQueries({
        queryKey: portalTreatmentCoursesQueryKey(activeClinicId),
      });
      void queryClient.invalidateQueries({
        queryKey: portalAppointmentsQueryKey(activePatientId),
      });
      if (course?.id) {
        queryClient.setQueryData(portalTreatmentCourseQueryKey(course.id), course);
      }
    },
  });
}
